import { defineComponent as _defineComponent } from 'vue'
import { getLotStatusClass } from "@/helpers/status";
  import { LotStatus } from "@/enums/LotStatus";
  import plural from "@/features/plural";
  import { ExtendedLot, LotListItem } from "@/v2/composables/useLotsData";
  import { ref, computed, onMounted, onUnmounted } from "vue";
  import {
    StatusFiltersLabels,
    StatusFiltersValues,
  } from "@/constants/statusFilters";
  import { UserTier } from "@/enums/UserTier";
  import { useStore } from "vuex";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";
  import StatusUpdateModal from "./StatusUpdateModal.vue";
  import BulkStatusUpdateModal from "./BulkStatusUpdateModal.vue";
  import EditBuyersModal from "./EditBuyersModal.vue";
  import ConditionsModal from "./ConditionsModal.vue";
  import LotsCardView from "./LotsCardView.vue";
  import CancelHoldModal from "./ui/modals/CancelHoldModal.vue";
  import SetHoldBuyersModal from "./ui/modals/SetHoldBuyersModal.vue";
  import BulkSetBuyersModal from "./ui/modals/BulkSetBuyersModal.vue";
  import LotDocumentsModal from "./ui/modals/LotDocumentsModal.vue";
  import { hasRequiredTier } from "@/helpers/getUserTier";
  import LotConditionsEnum from "@/v2/enums/LotConditions";
  import { getStatusText } from "@/v2/helpers/getStatusText";
  import { FileText } from "lucide-vue-next";

  interface Column {
    key: string;
    value: string;
    isSelected: boolean;
  }

  interface Filter {
    label: StatusFiltersLabels;
    value: StatusFiltersValues;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotsTable',
  props: {
    lots: {},
    selectedLotsList: {},
    showCreateOption: { type: Boolean },
    isLoading: { type: Boolean },
    isSelectAll: { type: Boolean },
    columnsArray: {},
    editingLot: {},
    editingField: {},
    editingValue: {},
    getFieldType: { type: Function },
    getColumnValue: { type: Function },
    getColumnDisplayValue: { type: Function },
    isFieldEditable: { type: Function },
    getFieldOptions: { type: Function },
    activeFilter: {},
    filters: {}
  },
  emits: [
    "clickAction",
    "toggleCustomizeColumns",
    "saveEdit",
    "cancelEdit",
    "startEditing",
    "updateEditingValue",
    "goBack",
    "changeFilter",
    "openEditPriceModal",
    "selectLot",
    "selectAll",
    "bulkStatusUpdate",
    "cancelHold",
    "bulkCancelHold",
    "setHold",
    "bulkSetHold",
    "bulkSetHoldWithBuyers",
    "refreshLotDocuments",
  ],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;

  const emit = __emit;

  const store = useStore();
  const scrollContainer = ref<HTMLElement | null>(null);
  const canScrollLeft = ref(false);
  const canScrollRight = ref(true);
  const canCustomizeColumns = ref(false);
  const showStatusModal = ref(false);
  const showBulkStatusModal = ref(false);
  const showBuyersModal = ref(false);
  const showConditionsModal = ref(false);
  const showCancelHoldModal = ref(false);
  const showSetHoldBuyersModal = ref(false);
  const showBulkSetBuyersModal = ref(false);
  const showDocumentsModal = ref(false);
  const selectedLotForDocuments = ref<ExtendedLot | null>(null);
  const pendingSetHoldLot = ref<ExtendedLot | null>(null);
  const pendingBulkSetHold = ref<LotListItem[]>([]);
  const pendingCancelHoldLot = ref<ExtendedLot | null>(null);
  const pendingBulkCancelHold = ref<LotListItem[]>([]);
  const pendingStatusWithBuyers = ref<string | null>(null);
  const selectedLot = ref<ExtendedLot | null>(null);
  const windowWidth = ref(window.innerWidth);
  const resizeHandler = ref<((event: Event) => void) | null>(null);

  const isEmpty = computed(() => !props.isLoading && props.lots.length === 0);
  const hasSelectedLots = computed(() => props.selectedLotsList.length > 0);
  const visibleColumns = computed(() =>
    props.columnsArray.filter((col) => col.isSelected)
  );
  const selectionText = computed(
    () => plural(props.selectedLotsList.length, "lot") + " selected"
  );
  const isMobile = computed(() => windowWidth.value < 730);
  const conditionsOptions = computed(() => Object.values(LotConditionsEnum));

  const displayStatus = (status: string) =>
    status === LotStatus.EOI ? "Pre-Contract" : status;
  const isActiveFilter = (value: string) => props.activeFilter.value === value;
  const isEditing = (lotId: string, field: string) =>
    props.editingLot === lotId && props.editingField === field;
  const getStatusForClass = (status: string) =>
    status === LotStatus.EOI ? LotStatus.PRE_CONTRACT : status;

  const cardViewIsEditing = (lotId: string, field: string) =>
    props.editingLot === lotId && props.editingField === field;

  const cardViewStartEdit = (lotId: string, field: string) => {
    if (props.isFieldEditable(field)) {
      const lot = props.lots.find((lot) => lot._id === lotId);

      if (lot) {
        if (field === "lotBuyers") {
          selectedLot.value = lot;
          showBuyersModal.value = true;
        } else if (field === "conditions") {
          openConditionsModal(lot);
        } else {
          emit("startEditing", lotId, field, props.getColumnValue(lot, field));
        }
      }
    }
  };

  const cardViewOpenEditPriceModal = (lot: ExtendedLot) => {
    emit("openEditPriceModal", lot);
  };

  const openStatusModal = (lot: ExtendedLot) => {
    selectedLot.value = lot;
    showStatusModal.value = true;
  };

  const closeStatusModal = () => {
    showStatusModal.value = false;
    selectedLot.value = null;
  };

  const closeBuyersModal = () => {
    showBuyersModal.value = false;
    selectedLot.value = null;
  };

  const openConditionsModal = (lot: ExtendedLot) => {
    selectedLot.value = lot;
    showConditionsModal.value = true;
  };

  const closeConditionsModal = () => {
    showConditionsModal.value = false;
    selectedLot.value = null;
  };

  const openDocumentsModal = (lot: ExtendedLot) => {
    selectedLotForDocuments.value = lot;
    showDocumentsModal.value = true;
  };

  const closeDocumentsModal = () => {
    showDocumentsModal.value = false;
    selectedLotForDocuments.value = null;
  };

  const handleDocumentsRefresh = () => {
    emit("refreshLotDocuments", selectedLotForDocuments.value?._id);
  };

  const openBulkStatusModal = () => {
    showBulkStatusModal.value = true;
  };

  const closeBulkStatusModal = () => {
    showBulkStatusModal.value = false;
  };

  const handleBulkStatusUpdate = (newStatus: string) => {
    // Check if changing away from statuses with buyers to Available or Not Released
    if (
      newStatus === LotStatus.AVAILABLE ||
      newStatus === LotStatus.NOT_RELEASED
    ) {
      const lotsWithBuyers = props.selectedLotsList.filter(
        (selected) =>
          selected.status === LotStatus.HOLD ||
          selected.status === LotStatus.UNDER_CONTRACT ||
          selected.status === LotStatus.UNCONDITIONAL
      );

      if (lotsWithBuyers.length > 0) {
        pendingBulkCancelHold.value = lotsWithBuyers;
        showCancelHoldModal.value = true;
        closeBulkStatusModal();
        return;
      }
    }

    if (
      newStatus === LotStatus.HOLD ||
      newStatus === LotStatus.UNDER_CONTRACT ||
      newStatus === LotStatus.UNCONDITIONAL
    ) {
      pendingBulkSetHold.value = props.selectedLotsList;
      pendingStatusWithBuyers.value = newStatus;
      showBulkSetBuyersModal.value = true;
      closeBulkStatusModal();
      return;
    }

    emit("bulkStatusUpdate", {
      selectedLots: props.selectedLotsList,
      newStatus: newStatus,
    });
  };

  const handleStatusUpdate = (newStatus: string) => {
    if (selectedLot.value) {
      if (
        (selectedLot.value.status === LotStatus.HOLD ||
          selectedLot.value.status === LotStatus.UNDER_CONTRACT ||
          selectedLot.value.status === LotStatus.UNCONDITIONAL) &&
        (newStatus === LotStatus.AVAILABLE ||
          newStatus === LotStatus.NOT_RELEASED)
      ) {
        pendingCancelHoldLot.value = selectedLot.value;
        showCancelHoldModal.value = true;
        closeStatusModal();
        return;
      }

      if (
        newStatus === LotStatus.HOLD ||
        newStatus === LotStatus.UNDER_CONTRACT ||
        newStatus === LotStatus.UNCONDITIONAL
      ) {
        pendingSetHoldLot.value = selectedLot.value;
        pendingStatusWithBuyers.value = newStatus;

        showSetHoldBuyersModal.value = true;
        closeStatusModal();
        return;
      }

      emit("startEditing", selectedLot.value._id, "status", "");
      emit("updateEditingValue", newStatus);
      emit("saveEdit");
    }
  };

  const closeCancelHoldModal = () => {
    showCancelHoldModal.value = false;
    pendingCancelHoldLot.value = null;
    pendingBulkCancelHold.value = [];
  };

  const closeSetHoldBuyersModal = () => {
    showSetHoldBuyersModal.value = false;
    pendingSetHoldLot.value = null;
    pendingBulkSetHold.value = [];
    pendingStatusWithBuyers.value = null;
  };

  const handleSetHoldConfirm = (buyers: {
    firstBuyer?: string;
    secondBuyer?: string;
  }) => {
    const newStatus = pendingStatusWithBuyers.value || LotStatus.HOLD;

    if (pendingSetHoldLot.value) {
      emit("setHold", {
        lot: pendingSetHoldLot.value,
        buyers: buyers,
        status: newStatus,
      });
    } else if (pendingBulkSetHold.value.length > 0) {
      emit("bulkSetHold", {
        lots: pendingBulkSetHold.value,
        buyers: buyers,
        status: newStatus,
      });
    }

    closeSetHoldBuyersModal();
  };

  const setHoldModalTitle = computed(() => {
    const status = pendingStatusWithBuyers.value;
    const statusText = getStatusText(status || "");

    if (pendingSetHoldLot.value) {
      return `Lot ${pendingSetHoldLot.value.title}`;
    }

    if (pendingBulkSetHold.value.length > 0) {
      const lotTitles = pendingBulkSetHold.value.map((lot) => {
        const fullLot = props.lots.find((l) => l._id === lot.id);

        return fullLot ? fullLot.title : lot.id;
      });

      if (lotTitles.length === 1) {
        return `Lot ${lotTitles[0]}`;
      }

      return `${lotTitles.length} Lots`;
    }

    return "";
  });

  const bulkLotsForBuyers = computed(() => {
    return pendingBulkSetHold.value.map((lot) => {
      const fullLot = props.lots.find((l) => l._id === lot.id);

      return {
        id: lot.id,
        title: fullLot ? fullLot.title : lot.id,
      };
    });
  });

  const existingBuyersForBulk = computed(() => {
    return pendingBulkSetHold.value.map((lot) => {
      const fullLot = props.lots.find((l) => l._id === lot.id);

      return {
        firstBuyer: fullLot?.lotBuyers?.firstBuyer || "",
        secondBuyer: fullLot?.lotBuyers?.secondBuyer || "",
      };
    });
  });

  const closeBulkSetBuyersModal = () => {
    showBulkSetBuyersModal.value = false;
    pendingBulkSetHold.value = [];
    pendingStatusWithBuyers.value = null;
  };

  const handleBulkSetBuyersConfirm = (
    buyersData: Array<{ firstBuyer: string; secondBuyer: string }>
  ) => {
    const newStatus = pendingStatusWithBuyers.value || LotStatus.HOLD;

    emit("bulkSetHoldWithBuyers", {
      lots: pendingBulkSetHold.value,
      buyersData: buyersData,
      status: newStatus,
    });
    closeBulkSetBuyersModal();
  };

  const handleBulkSetBuyersSkip = () => {
    const newStatus = pendingStatusWithBuyers.value || LotStatus.HOLD;

    emit("bulkStatusUpdate", {
      selectedLots: pendingBulkSetHold.value,
      newStatus: newStatus,
    });
    closeBulkSetBuyersModal();
  };

  const handleCancelHoldConfirm = () => {
    if (pendingCancelHoldLot.value) {
      emit("cancelHold", pendingCancelHoldLot.value);
    } else if (pendingBulkCancelHold.value.length > 0) {
      const nonHoldLots = props.selectedLotsList.filter(
        (selected) => selected.status !== LotStatus.HOLD
      );

      emit("bulkCancelHold", {
        holdLots: pendingBulkCancelHold.value,
        nonHoldLots: nonHoldLots,
      });
    }

    closeCancelHoldModal();
  };

  const cancelHoldModalTitle = computed(() => {
    if (pendingCancelHoldLot.value) {
      return `Lot ${pendingCancelHoldLot.value.title}`;
    }

    if (pendingBulkCancelHold.value.length > 0) {
      const lotTitles = pendingBulkCancelHold.value.map((lot) => {
        const fullLot = props.lots.find((l) => l._id === lot.id);

        return fullLot ? fullLot.title : lot.id;
      });

      if (lotTitles.length === 1) {
        return `Lot ${lotTitles[0]}`;
      }

      return `${lotTitles.length} Lots`;
    }

    return "";
  });

  const cancelHoldCurrentStatus = computed(() => {
    if (pendingCancelHoldLot.value) {
      return pendingCancelHoldLot.value.status;
    }

    if (
      pendingBulkCancelHold.value.length > 0 &&
      pendingBulkCancelHold.value[0]
    ) {
      const firstLot = props.lots.find(
        (l) => l._id === pendingBulkCancelHold.value[0].id
      );

      return firstLot?.status || LotStatus.HOLD;
    }

    return LotStatus.HOLD;
  });

  const handleBuyersUpdate = (buyers: {
    firstBuyer?: string;
    secondBuyer?: string;
  }) => {
    if (selectedLot.value) {
      emit("startEditing", selectedLot.value._id, "lotBuyers", "");
      emit("updateEditingValue", JSON.stringify(buyers));
      emit("saveEdit");
    }
  };

  const handleConditionsUpdate = (newCondition: string) => {
    if (selectedLot.value) {
      emit("startEditing", selectedLot.value._id, "conditions", "");
      emit("updateEditingValue", newCondition);
      emit("saveEdit");
    }
  };

  const startEdit = (lotId: string, field: string) => {
    if (props.isFieldEditable(field)) {
      const lot = props.lots.find((lot) => lot._id === lotId);

      if (lot) {
        if (field === "lotBuyers") {
          selectedLot.value = lot;
          showBuyersModal.value = true;
        } else if (field === "conditions") {
          openConditionsModal(lot);
        } else {
          emit("startEditing", lotId, field, props.getColumnValue(lot, field));
        }
      }
    }
  };

  const handleCustomizeClick = () => {
    if (canCustomizeColumns.value) {
      emit("toggleCustomizeColumns");
    }
  };

  const updateScrollState = () => {
    if (!scrollContainer.value) return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainer.value;

    canScrollLeft.value = scrollLeft > 0;
    canScrollRight.value = scrollLeft < scrollWidth - clientWidth;
  };

  const scrollLeft = () => scroll(-150);
  const scrollRight = () => scroll(150);

  const scroll = (amount: number) => {
    if (!scrollContainer.value) return;

    const currentScroll = scrollContainer.value.scrollLeft;
    const newScroll =
      amount < 0 ? Math.max(0, currentScroll + amount) : currentScroll + amount;

    scrollContainer.value.scrollLeft = newScroll;
  };

  onMounted(async () => {
    setTimeout(updateScrollState, 100);
    canCustomizeColumns.value = await hasRequiredTier(
      store.state.user.data,
      UserTier.TIER_2
    );

    const handleResize = () => {
      windowWidth.value = window.innerWidth;
    };

    resizeHandler.value = handleResize;
    window.addEventListener("resize", handleResize);
  });

  onUnmounted(() => {
    if (resizeHandler.value) {
      window.removeEventListener("resize", resizeHandler.value);
      resizeHandler.value = null;
    }
  });

const __returned__ = { props, emit, store, scrollContainer, canScrollLeft, canScrollRight, canCustomizeColumns, showStatusModal, showBulkStatusModal, showBuyersModal, showConditionsModal, showCancelHoldModal, showSetHoldBuyersModal, showBulkSetBuyersModal, showDocumentsModal, selectedLotForDocuments, pendingSetHoldLot, pendingBulkSetHold, pendingCancelHoldLot, pendingBulkCancelHold, pendingStatusWithBuyers, selectedLot, windowWidth, resizeHandler, isEmpty, hasSelectedLots, visibleColumns, selectionText, isMobile, conditionsOptions, displayStatus, isActiveFilter, isEditing, getStatusForClass, cardViewIsEditing, cardViewStartEdit, cardViewOpenEditPriceModal, openStatusModal, closeStatusModal, closeBuyersModal, openConditionsModal, closeConditionsModal, openDocumentsModal, closeDocumentsModal, handleDocumentsRefresh, openBulkStatusModal, closeBulkStatusModal, handleBulkStatusUpdate, handleStatusUpdate, closeCancelHoldModal, closeSetHoldBuyersModal, handleSetHoldConfirm, setHoldModalTitle, bulkLotsForBuyers, existingBuyersForBulk, closeBulkSetBuyersModal, handleBulkSetBuyersConfirm, handleBulkSetBuyersSkip, handleCancelHoldConfirm, cancelHoldModalTitle, cancelHoldCurrentStatus, handleBuyersUpdate, handleConditionsUpdate, startEdit, handleCustomizeClick, updateScrollState, scrollLeft, scrollRight, scroll, get getLotStatusClass() { return getLotStatusClass }, get LotStatus() { return LotStatus }, get plural() { return plural }, get ExtendedLot() { return ExtendedLot }, get LotListItem() { return LotListItem }, ref, computed, onMounted, onUnmounted, get StatusFiltersLabels() { return StatusFiltersLabels }, get StatusFiltersValues() { return StatusFiltersValues }, get UserTier() { return UserTier }, get useStore() { return useStore }, DropDown, StatusUpdateModal, BulkStatusUpdateModal, EditBuyersModal, ConditionsModal, LotsCardView, CancelHoldModal, SetHoldBuyersModal, BulkSetBuyersModal, LotDocumentsModal, get hasRequiredTier() { return hasRequiredTier }, get LotConditionsEnum() { return LotConditionsEnum }, get getStatusText() { return getStatusText }, get FileText() { return FileText } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})